import { createContext, useCallback, useEffect, useState, type ReactNode } from "react";
import { THEME_KEY } from "@/lib/constants";

type Theme = "dark" | "light";

interface ThemeContextValue {
  theme: Theme;
  toggleTheme: () => void;
}

export const ThemeContext = createContext<ThemeContextValue | null>(null);

function getPreferredTheme(): Theme {
  if (typeof window === "undefined") return "light";
  const stored = localStorage.getItem(THEME_KEY);
  if (stored === "dark" || stored === "light") return stored;
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function applyThemeToDOM(theme: Theme) {
  document.documentElement.classList.toggle("dark", theme === "dark");
  localStorage.setItem(THEME_KEY, theme);
  const favicon = document.getElementById("favicon") as HTMLLinkElement | null;
  if (favicon) {
    favicon.href = theme === "dark" ? "/favicon-dark.svg" : "/favicon-light.svg";
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  // Start with "light" to match SSR. The inline script in index.html already
  // set the correct dark class + favicon before React loads, so DOM is correct
  // from the first frame. This effect only syncs React state.
  const [theme, setTheme] = useState<Theme>("light");

  useEffect(() => {
    setTheme(getPreferredTheme());
  }, []);

  // No [theme] effect — DOM is managed exclusively by:
  // 1. The inline script in index.html (on page load)
  // 2. toggleTheme (on user interaction)

  const toggleTheme = useCallback(() => {
    const root = document.documentElement;
    root.classList.add("theme-switching");
    setTheme((t) => {
      const next = t === "dark" ? "light" : "dark";
      applyThemeToDOM(next);
      return next;
    });
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        root.classList.remove("theme-switching");
      });
    });
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}
